// AdminTallyReset.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronDown, ChevronUp, HelpCircle, RefreshCw, Save, ChevronLeft} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminWrapper from "../components/AdminWrapper";

/**
 * Admin Tally Reset (ASPX -> React)
 *
 * Inventory DB:
 * - branches dropdown (usp_getbranchddl)
 * - get ids (usp_admin_GetTallyIds)
 *
 * Tally DB:
 * - update ids (usp_admin_UpdateTallyAutoIds)
 * - insert log (usp_admin_inserttallyresetdetails)
 *
 * APIs:
 * GET  /api/admin-tally-reset/branches
 * POST /api/admin-tally-reset/reset
 */
const AdminTallyReset = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);

  const navigate = useNavigate();

const handleBack = () => {
  if (saving) return;
  navigate(-1);
};
  const [showForm, setShowForm] = useState(true);

  // dropdown
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("Select");

  // type
  const [type, setType] = useState("Select"); // BILLPASS / STNO / STNI

  // reset numbers
  const [nos, setNos] = useState("");

  const [loadingBranches, setLoadingBranches] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ IMPORTANT: must be same key you are storing at login for tally/inventory selection.
  // In your project you used finYearId too, but this API needs finYear string "2025-2026".
  // So store "finYear" in localStorage like: localStorage.setItem("finYear","2025-2026")
 const finYear = (localStorage.getItem("finYear") || "").trim();

  const headers = useMemo(() => (finYear ? { "x-finyear": finYear } : {}), [finYear]);

  // ✅ Admin ID: should be admin_credential_id (FK target) not adminUserID
  const adminCredId = useMemo(() => {
    try {
      const admin = JSON.parse(localStorage.getItem("admin") || "null");
      // node login returns: admin.id = admin_credential_id
      const id = Number(admin?.id || 0);
      if (id) return id;
    } catch {}
    // fallback keys if you already stored them
    const raw1 = localStorage.getItem("adminCredId");
    if (raw1) {
      try {
        const v = JSON.parse(raw1);
        const id = Number(v || 0);
        if (id) return id;
      } catch {}
      const id = Number(raw1 || 0);
      if (id) return id;
    }
    const raw2 = localStorage.getItem("adminId");
    if (raw2) {
      try {
        const v = JSON.parse(raw2);
        const id = Number(v || 0);
        if (id) return id;
      } catch {}
      const id = Number(raw2 || 0);
      if (id) return id;
    }
    return 0;
  }, []);

  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);
      const res = await axios.get(`${BASE_URL}/api/admin-tally-reset/branches`, { headers });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load branches");
        return;
      }

      // usp_getbranchddl usually returns: fld_branchid, fld_BranchName
      setBranches(res.data.list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load branches");
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    if (!finYear) {
      toast.error("finYear missing in localStorage. Please set finYear like 2025-2026.");
      return false;
    }
    if (branchId === "Select") {
      toast.error("Please select Branch");
      return false;
    }
    if (type === "Select") {
      toast.error("Please select Type");
      return false;
    }
    if (!String(nos || "").trim()) {
      toast.error("Please Enter Reset Numbers");
      return false;
    }
    if (!adminCredId) {
      toast.error("AdminID missing! Please login again.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/admin-tally-reset/reset`,
        {
          branchId: Number(branchId),
          type,
          nos: String(nos || "").trim(),
          adminId: adminCredId,
        },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Reset failed");
        return;
      }

      toast.success(res.data?.message || "Reseted Successfully");

      // reset form
      setBranchId("Select");
      setType("Select");
      setNos("");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Tally Reset</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4">
              <button
      type="button"
      onClick={handleBack}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      } disabled:opacity-60`}
    >
      <ChevronLeft className="w-5 h-5" />
      Back
    </button>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tally Reset
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {finYear ? `FinYear: ${finYear}` : "FinYear: missing"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open("/admin/help/tally-reset", "_blank")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <HelpCircle className="w-5 h-5" />
                Help
              </button>

              <button
                type="button"
                onClick={() => {
                  fetchBranches();
                  toast.info("Refreshed");
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showForm && (
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                <div className="grid grid-cols-12 gap-4 items-start max-w-5xl">
                  {/* Branch */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">
                      Branch <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={branchId}
                      onChange={(e) => setBranchId(e.target.value)}
                      disabled={saving || loadingBranches}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    >
                      <option value="Select">
                        {loadingBranches ? "Loading..." : "Select"}
                      </option>

                      {branches.map((b) => {
                        const id = b.fld_branchid ?? b.fld_Branchid ?? b.BranchId ?? b.branchId;
                        const name = b.fld_BranchName ?? b.BranchName ?? b.branchName ?? "";
                        return (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Type */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      disabled={saving}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    >
                      <option value="Select">Select</option>
                      <option value="BILLPASS">BILL PASS</option>
                      <option value="STNO">STN OUTWARD</option>
                      <option value="STNI">STN INWARD</option>
                    </select>
                  </div>

                  {/* Reset Numbers */}
                  <div className="col-span-12">
                    <label className="text-sm font-medium block mb-1">
                      Reset Numbers <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={nos}
                      onChange={(e) => setNos(e.target.value)}
                      disabled={saving}
                      rows={4}
                      placeholder="Enter numbers (multiline textbox)"
                      className={`w-full px-3 py-2 rounded-lg border resize-y ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    />
                    <div className={`text-xs mt-1 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                      Tip: Paste multiple numbers separated by comma / space / new line.
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="col-span-12 flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                    >
                      <Save className="w-5 h-5" />
                      {saving ? "Submitting..." : "Submit"}
                    </button>
                  </div>

                  {/* Debug small line (optional) */}
                  <div className="col-span-12">
                    <div className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
                      AdminCredId: <span className="font-semibold">{adminCredId || "missing"}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminWrapper>
  );
};

export default AdminTallyReset;
